import * as langCSS from './components/LanguagePicker/LanguagePopup.scss';
import * as headerNavCSS from './components/Header/Navigation/Navigation.scss';
import * as headerCSS from './components/Header/Header.scss';
import { LanguagePopup } from './components/LanguagePicker/LanguagePopup';
import { UserMenu } from './components/Header/Navigation/UserMenu';
import { userMenuData } from './data/userMenu';
import {
  BiParams,
  headerAndFooterBiLogger,
  hfBiConfig,
  logHeaderLoaded,
  MOBILE_MAX_WIDTH,
  wrapLangLinkWithBiEvent,
  wrapLinkWithBiEvent,
} from './utils/bi';
import { getUserImageUrl, getUserProfileUrl, request } from './utils/request';
import { $, $$, getMainMenuWrapper, getHeader, getDesktopLoginLink, getNavigationMenus, getAccountMenuButton } from './utils/selectors';
import { createTranslator, Translator } from './utils/translate';
import Header from './components/Header';
import { MainMenuItem } from './components/Header/Navigation/MainMenuItem';
import { languageMenuItemData } from './components/Header/Navigation/Languages';
import { SubMenu } from './components/Header/Navigation/SubMenu';
import { setLanguageUrl } from './utils/languageUrl';
import { findClosestElementBySelector, removeElement } from './utils/polyfills';
import { collapseAllMenus } from './utils/menus';
import { initGoogleBotLogic } from './utils/bots';
import { getConfig, HFConfig } from './config';
import * as fetch from './utils/fetch';
import { getHeaderStructureByLanguage } from './data/header';

export interface HeaderFooterState {
  isDesktop: boolean;
  trapFocusHandler: ((e: KeyboardEvent) => void) | null;
  hoverStartTime: Map<string | undefined, number>;
}

let state: HeaderFooterState;
let t: Translator;

export async function init(overwriteConfig?: Partial<HFConfig>) {
  const wixHeader = getHeader();
  const config = getConfig(overwriteConfig);
  const headerStructure = getHeaderStructureByLanguage(config.language);
  if (!headerStructure) {
    throw new Error(`Wix Header 2020 doesn't support language "${config.language}"`);
  }
  const messages = await fetch.translations(config);
  t = createTranslator(messages);
  if (!wixHeader) {
    if (config.headerWrapper) {
      config.headerWrapper.innerHTML = Header({ t, structure: headerStructure, config });
      config.headerWrapper.replaceWith(getHeader() || '');
    } else {
      document.body.insertAdjacentHTML('afterbegin', Header({ t, structure: headerStructure, config }));
    }
  }
  if (!window.WIX_HF_INITIALIZED) {
    window.WIX_HF_INITIALIZED = true;
    state = initState();
    initHeader(config);
    disableFocusForMouseEvents();
  }
}

function initState(): HeaderFooterState {
  return {
    isDesktop: window.innerWidth > MOBILE_MAX_WIDTH,
    trapFocusHandler: null,
    hoverStartTime: new Map(),
  };
}

function initHeader(config: HFConfig) {
  if (!config.headerWrapper) {
    moveHeaderToTop();
  }
  renderMobileLanguageList(config);
  logHeaderLoaded();
  enableHeaderToggles(config);
  enableHeaderMenuBiEvents();
  loadUserDetails(config);
  shadowForSignInContainer();
  enableOverlayClick();
  addOriginParamToLoginLink();
  addEventListenersToCategoryMenuButtons();

  if (config.sticky) {
    makeHeaderSticky();
  }

  if (config.isSupportSite) {
    makeHeaderWithoutHeight();
  }

  if (config.hideMainLinksOnDesktop) {
    hideMainLinksOnDesktop();
  }

  initGoogleBotLogic(state);
}

function disableFocusForMouseEvents() {
  const headerElement = getHeader();
  const footerElement = $('#WIX_FOOTER');
  window.addEventListener('keydown', handleFirstTab);
  window.addEventListener('mousedown', () => {
    setTimeout(() => {
      if (
        (headerElement && headerElement.contains(document.activeElement)) ||
        (footerElement && footerElement.contains(document.activeElement))
      ) {
        // @ts-ignore
        document.activeElement.blur();
      }
    }, 0);
  });
}

function loadUserDetails(config: HFConfig) {
  request(getUserProfileUrl(), renderUserMenu(config));
}

function moveHeaderToTop() {
  const header = getHeader();
  if (header) {
    document.body.insertBefore(header, document.body.firstChild);
  }
}

function enableHeaderToggles(config: HFConfig) {
  enableMobileMenuToggle();
  enableMenuToggles();
  enableSubMenuToggles();
  enableLanguagePicker(config);
  enableSubMenuSelectors();
  changeTabIndexOfSecondLevelButtons();

  document.addEventListener('mousedown', handleClickOutside);
  window.addEventListener('resize', handleWindowResize);
}

function handleClickOutside(event: any) {
  const header = getHeader();
  if (header && !header.contains(event.target)) {
    collapseAllMenus();
  }
}

function enableMobileMenuToggle() {
  const menuToggle = $('header [data-hook=header-menu-toggle]') as HTMLElement;
  if (menuToggle) {
    menuToggle.addEventListener('click', () => {
      const expanded = menuToggle.dataset.expanded === 'true' ? 'false' : 'true';
      if (expanded === 'true') {
        openMobileMenu();
      } else {
        closeMobileMenu();
      }
    });
    stopClickPropagation(menuToggle);
    fireClickEventWhenSpaceIsPressed(menuToggle);
  }
}

function openMobileMenu() {
  const menuToggle = $('header [data-hook=header-menu-toggle]') as HTMLElement;
  if (menuToggle) {
    menuToggle.dataset.expanded = 'true';
    menuToggle.setAttribute('aria-expanded', 'true');
    menuToggle.title = t('close_navigation');
    menuToggle.setAttribute('aria-label', t('close_navigation'));
    showOverlay();
  }
  const mainHeaderWrapper = getHeader();
  if (mainHeaderWrapper) {
    mainHeaderWrapper.classList.add(headerCSS.inFrontOfCookieBanner);
  }
}

function closeMobileMenu() {
  const menuToggle = $('header [data-hook=header-menu-toggle]') as HTMLElement;
  if (menuToggle) {
    menuToggle.dataset.expanded = 'false';
    menuToggle.setAttribute('aria-expanded', 'false');
    menuToggle.title = t('open_navigation');
    menuToggle.setAttribute('aria-label', t('open_navigation'));
    hideOverlay();
    collapseAllMenus();
  }
  const mainHeaderWrapper = getHeader();
  if (mainHeaderWrapper) {
    mainHeaderWrapper.classList.remove(headerCSS.inFrontOfCookieBanner);
  }
}

function enableMenuToggles() {
  const menuItemToggles = $$(`.${headerNavCSS.menuItem} > .${headerNavCSS.linkWrapper} [data-toggle=collapse]`);
  menuItemToggles.forEach((toggle) => {
    const linkWrapper = toggle.parentElement as Element;
    linkWrapper.addEventListener('click', () => {
      const container = linkWrapper.parentElement as HTMLLIElement;
      const expanded = container.dataset.expanded === 'true' ? 'false' : 'true';
      const subMenuElement = container.querySelector(`.${headerNavCSS.subMenu}`);
      collapseAllMenus();
      if (subMenuElement) {
        resetSubMenuActiveElements(subMenuElement, expanded === 'true');
      }

      const biParams = getHeaderBiParams(toggle as any);
      biParams.tab = biParams.button_name;
      delete biParams.button_name;
      if (expanded === 'true') {
        setMenuExpanded(true);
        headerAndFooterBiLogger.log({
          ...biParams,
          action: 'open_tab',
        });
      } else {
        setMenuExpanded(false);
        headerAndFooterBiLogger.log({
          ...biParams,
          action: 'close_tab',
        });
      }

      toggle.setAttribute('aria-expanded', expanded);
      container.setAttribute('data-expanded', expanded);
      if (window.innerWidth <= MOBILE_MAX_WIDTH) {
        setTimeout(
          () =>
            container.scrollIntoView({
              behavior: 'smooth',
            }),
          300,
        );
      }
      shadowForSignInContainer();
    });
  });
}

function setMenuExpanded(isExpanded: boolean) {
  const menu = $(`.${headerNavCSS.menuWrapper}`);
  if (menu) {
    if (isExpanded) {
      if (window.innerWidth > MOBILE_MAX_WIDTH) {
        showOverlay();
      }
      menu.classList.add(headerNavCSS.expandedMenu);
    } else {
      if (window.innerWidth > MOBILE_MAX_WIDTH) {
        hideOverlay();
      }
      menu.classList.remove(headerNavCSS.expandedMenu);
    }
  }
}

function enableSubMenuToggles() {
  const subMenuItemToggles = $$(`.${headerNavCSS.subMenuItem} >.${headerNavCSS.linkWrapper} [data-toggle=collapse]`);
  subMenuItemToggles.forEach((toggle) => {
    const linkWrapper = toggle.parentElement!;
    linkWrapper.addEventListener('click', () => {
      if (window.innerWidth <= MOBILE_MAX_WIDTH) {
        const container = linkWrapper.parentElement as HTMLLIElement;
        const expanded = container.dataset.expanded === 'true' ? 'false' : 'true';

        subMenuItemToggles.forEach((item) => {
          if (item !== toggle) {
            const itemContainer = item.parentElement!.parentElement as HTMLLIElement;
            item.setAttribute('aria-expanded', 'false');
            itemContainer.setAttribute('data-expanded', 'false');
          }
        });

        toggle.setAttribute('aria-expanded', expanded);
        container.setAttribute('data-expanded', expanded);
        setTimeout(
          () =>
            container.scrollIntoView({
              behavior: 'smooth',
            }),
          300,
        );
      }
      shadowForSignInContainer();
    });
  });
}

function enableSubMenuSelectors() {
  const subMenuItems = $$(`.${headerNavCSS.subMenuItem}.${headerNavCSS.hasItems}`) as Element[];
  subMenuItems.forEach((item) => {
    const button = item.querySelector('[data-hook="wix-header-open-third-menu"]')!;
    const linkWrapper = button.parentElement!;
    linkWrapper.addEventListener('mouseenter', () => {
      if (state.isDesktop) {
        changeSelectedElement(item, button);
      }
    });
    const linkElements = $$(`.${headerNavCSS.subSubMenu} .${headerNavCSS.link}`, item);
    linkElements.forEach((linkElement) => {
      linkElement.addEventListener('focus', () => {
        if (!isSelectedSubMenuItem(item) && state.isDesktop) {
          changeSelectedElement(item, button);
        }
      });
    });
  });

  function changeSelectedElement(item: Element, button: Element) {
    Array.from(item.parentElement!.children).forEach((menuItem) => {
      if (isSelectedSubMenuItem(menuItem)) {
        unSelectSubMenuItem(menuItem);
      }
    });
    selectSubMenuItem(item);
  }
}

function unSelectSubMenuItem(subMenuItem: Element) {
  subMenuItem.classList.remove(headerNavCSS.selected);
  const itemButton = subMenuItem.querySelector('[data-hook="wix-header-open-third-menu"]');
  if (itemButton) {
    itemButton.setAttribute('aria-expanded', 'false');
  }
  subMenuItem.setAttribute('data-expanded', 'false');
}

function selectSubMenuItem(subMenuItem: Element) {
  subMenuItem.classList.add(headerNavCSS.selected);
  const itemButton = subMenuItem.querySelector('[data-hook="wix-header-open-third-menu"]');
  if (itemButton) {
    itemButton.setAttribute('aria-expanded', 'true');
  }
  subMenuItem.setAttribute('data-expanded', 'true');
}

function isSelectedSubMenuItem(item: Element) {
  return item.classList.contains(headerNavCSS.selected);
}

function enableLanguagePicker(config: HFConfig) {
  const wixHeader = getHeader();
  const languageToggles = $$('header [data-hook=language-picker]');
  if (wixHeader && languageToggles) {
    const languageTogglesArray = Array.from(languageToggles) as HTMLAnchorElement[];
    languageTogglesArray.forEach((item) => {
      const linkWrapper = item.parentElement as HTMLElement;
      linkWrapper.addEventListener('click', () => {
        wixHeader.insertAdjacentHTML('beforeend', LanguagePopup({ config }));
        const languagePopup = $('header [data-hook=language-popup]') as HTMLElement | null;
        const languageList = $('header [data-hook=language-list]');

        if (languagePopup && languageList) {
          const firstFocusableElement = languageList.children[0].querySelector(`.${langCSS.link}`) as HTMLAnchorElement;
          const lastFocusableElement = languagePopup.querySelector('[data-hook="close-language-picker"]') as HTMLElement;

          if (isFocusEnabled()) {
            firstFocusableElement.focus();
          }
          const handler = trapFocus(firstFocusableElement, lastFocusableElement);

          languagePopup.addEventListener('click', () => {
            removeTrapFocus(handler);
            removeElement(languagePopup);
            if (isFocusEnabled()) {
              item.focus();
            }
          });
          stopClickPropagation(languagePopup);

          languageList.addEventListener('click', (e) => {
            e.stopPropagation();
          });
        }

        const biParams = getHeaderBiParams(item);
        biParams.tab = biParams.button_name;
        delete biParams.button_name;
        headerAndFooterBiLogger.log({
          ...biParams,
          action: 'open_tab',
        });

        const languageLinks = $$(`.${langCSS.biLink}`);
        if (languageLinks) {
          const languageLinksArray = Array.from(languageLinks) as HTMLAnchorElement[];
          enableBiEventsOnLinks(languageLinksArray);
          languageLinksArray.forEach((link) => {
            wrapLangLinkWithBiEvent(headerAndFooterBiLogger, link, link.dataset.biName || '');
          });
        }

        addEventListenersToCloseLanguagePickerButton(biParams);
      });
      fireClickEventWhenSpaceIsPressed(item);
      stopClickPropagation(linkWrapper);
    });
  }
}

function addEventListenersToCloseLanguagePickerButton(biParams: BiParams) {
  const closeButton = $('[data-hook="close-language-picker"]') as HTMLAnchorElement;
  enableBiEventsOnLinks([closeButton]);
  closeButton.addEventListener('click', () => {
    headerAndFooterBiLogger.log({
      ...biParams,
      action: 'close_tab',
    });
  });
  fireClickEventWhenSpaceIsPressed(closeButton);
}

function shadowForSignInContainer() {
  setTimeout(() => {
    const menu = $(`.${headerNavCSS.menu}`);
    if (menu) {
      const menuContainer = menu.parentElement;
      if (menuContainer) {
        const hasScrollbar = menu.scrollHeight > menuContainer.clientHeight - 80;
        const signInContainer = $(`.${headerNavCSS.signInContainer}`);
        if (signInContainer) {
          if (hasScrollbar) {
            signInContainer.classList.add(headerNavCSS.hasShadow);
          } else {
            signInContainer.classList.remove(headerNavCSS.hasShadow);
          }
        }
      }
    }
  }, 200);
}

function renderUserMenu(config: HFConfig) {
  return (userResponse: any) => {
    const container = document.querySelector(`.${headerNavCSS.menu}`);
    const userName = userResponse.payload.userName;

    document.body.classList.add('logged_in');

    const loginButton = document.querySelector(`.${headerNavCSS.signInContainer}`) as HTMLElement | null;
    if (loginButton) {
      removeElement(loginButton);
    }

    if (container) {
      const mySites = MainMenuItem({ item: userMenuData.mySites, t, language: config.language });
      container.insertAdjacentHTML('afterbegin', mySites);
      request(
        getUserImageUrl(),
        (response) => {
          const userImage = response.profileImage;
          const userMenu = UserMenu({
            navigation: userMenuData.userNav,
            t,
            userImage,
            userName,
            language: config.language,
          });
          if (state.isDesktop) {
            container.insertAdjacentHTML('beforeend', userMenu);
          } else {
            container.insertAdjacentHTML('afterbegin', userMenu);
          }
          enableUserMenuBiEvents();
          enableUserMenuToggle();
          enableLanguagePicker(config);
        },
        () => {
          const userMenu = UserMenu({
            navigation: userMenuData.userNav,
            t,
            userName,
            language: config.language,
          });
          if (state.isDesktop) {
            container.insertAdjacentHTML('beforeend', userMenu);
          } else {
            container.insertAdjacentHTML('afterbegin', userMenu);
          }
          enableUserMenuBiEvents();
          enableUserMenuToggle();
          enableLanguagePicker(config);
        },
      );
    }
  };
}

function enableUserMenuToggle() {
  const toggle = getAccountMenuButton();
  if (toggle) {
    const linkWrapper = toggle.parentElement!;
    linkWrapper.addEventListener('click', () => {
      const container = linkWrapper.parentElement as HTMLLIElement;
      const expanded = !(container.dataset.expanded === 'true');
      if (window.innerWidth >= MOBILE_MAX_WIDTH) {
        const biParams = getHeaderBiParams(toggle as any);
        biParams.tab = biParams.button_name;
        delete biParams.button_name;

        if (expanded) {
          setMenuExpanded(false);
          headerAndFooterBiLogger.log({
            ...biParams,
            action: 'open_tab',
          });
        } else {
          headerAndFooterBiLogger.log({
            ...biParams,
            action: 'close_tab',
          });
        }

        collapseAllMenus();
      }
      toggle.setAttribute('aria-expanded', expanded.toString());
      container.setAttribute('data-expanded', expanded.toString());
    });
    fireClickEventWhenSpaceIsPressed(toggle);
    stopClickPropagation(linkWrapper);
  }
}

function renderMobileLanguageList({ languageRedirectUrl, language }: HFConfig) {
  const languageSubMenuWrapper = $('[data-hook="wix-header-language-submenu-wrapper"]') as HTMLElement;

  if (languageSubMenuWrapper) {
    const { id, subs } = languageMenuItemData;
    const items = subs
      .filter(({ name }) => name !== language)
      .map((sub) => ({
        ...sub,
        href: setLanguageUrl({
          language: sub.name || '',
          location: window.location,
          languageRedirectUrl,
        }),
      }));

    languageSubMenuWrapper.innerHTML = SubMenu({
      labelledBy: id,
      items,
      t,
      language,
    });
  }
}

function getHeaderBiParams(link: HTMLAnchorElement): BiParams {
  const row = link.dataset.biRow || '';
  const params: BiParams = {
    evid: hfBiConfig.headerActions,
    action: 'click',
    from: window.location.href,
  };
  if (link.href && link.getAttribute('role') !== 'button') {
    params.to = link.href;
  }
  const button_name = link.dataset.biName || '';
  if (row === 'languages') {
    return {
      ...params,
      button_name,
      tab: row,
    };
  }
  if (row === 'first') {
    return {
      ...params,
      button_name,
    };
  }
  if (row === 'second') {
    const menuItem = findClosestElementBySelector(link, `.${headerNavCSS.menuItem}`);
    let tab = '';
    if (menuItem) {
      const heading = menuItem.querySelector<HTMLAnchorElement>(`.${headerNavCSS.link}`);
      if (heading) {
        tab = heading.dataset.biName || '';
      }
    }
    return {
      ...params,
      button_name,
      tab,
    };
  }

  const menuItem = findClosestElementBySelector(link, `.${headerNavCSS.menuItem}`);
  const subMenuItem = findClosestElementBySelector(link, `.${headerNavCSS.subMenuItem}`);
  let tab = '';
  let sub_tab = '';
  if (menuItem) {
    const heading = menuItem.querySelector<HTMLAnchorElement>(`.${headerNavCSS.link}`);
    if (heading) {
      tab = heading.dataset.biName || '';
    }
  }
  if (subMenuItem) {
    const heading = subMenuItem.querySelector<HTMLAnchorElement>(`.${headerNavCSS.link}`);
    if (heading) {
      sub_tab = heading.dataset.biName || '';
    }
  }
  return {
    ...params,
    button_name,
    sub_tab,
    tab,
  };
}

function enableUserMenuBiEvents() {
  const elements = $$(`.${headerNavCSS.userMenu} .${headerNavCSS.link}`) as HTMLAnchorElement[];
  const mySitesButton = $(`[data-bi-name=${userMenuData.mySites.name}]`) as HTMLAnchorElement | null;
  if (mySitesButton) {
    elements.push(mySitesButton);
  }
  enableBiEventsOnLinks(elements);
}

function enableHeaderMenuBiEvents() {
  const linksElements = $$(`.${headerNavCSS.biLink}`) as HTMLAnchorElement[];
  enableBiEventsOnLinks(linksElements);
  const wrapperLinkElements = $$(`.${headerNavCSS.linkWrapper}`) as HTMLDivElement[];
  enableBiEventsOnLinks(wrapperLinkElements, `.${headerNavCSS.link}`);
  const langLinks = $$(`[data-hook="wix-header-language-submenu-wrapper"] .${headerNavCSS.biLink}`) as HTMLAnchorElement[];
  langLinks.forEach((link) => {
    wrapLangLinkWithBiEvent(headerAndFooterBiLogger, link, link.dataset.biName || '');
  });
}

function enableBiEventsOnLinks(elements: HTMLElement[], linkSelector?: string) {
  elements.forEach((element) => {
    const link = (linkSelector ? element.querySelector(linkSelector) : element) as HTMLAnchorElement;
    wrapLinkWithBiEvent(headerAndFooterBiLogger, link, element, getHeaderBiParams);

    element.addEventListener('mouseenter', () => {
      const name = link.dataset.biName;
      const time = new Date().getTime();
      state.hoverStartTime.set(name, time);
    });

    element.addEventListener('mouseleave', () => {
      const name = link.dataset.biName;
      const time = new Date().getTime();
      const startTime = state.hoverStartTime.get(name);
      if (startTime) {
        headerAndFooterBiLogger.log({
          ...getHeaderBiParams(link),
          hover_ms: time - startTime,
          action: 'hover',
        });
      }
    });
  });
}

function handleFirstTab(e: KeyboardEvent) {
  if (e.keyCode === 9) {
    document.body.classList.add('focused');

    window.removeEventListener('keydown', handleFirstTab);
    window.addEventListener('mousedown', handleMouseDownOnce);
  }
}

function handleMouseDownOnce() {
  document.body.classList.remove('focused');

  window.removeEventListener('mousedown', handleMouseDownOnce);
  window.addEventListener('keydown', handleFirstTab);
}

function makeHeaderSticky() {
  const headerElement = $('[data-wix-header=hf2020] > div');
  if (headerElement) {
    headerElement.classList.add(headerCSS.fixed);
  }
}

function resetSubMenuActiveElements(subMenu: Element, isExpandedMenuItem: boolean) {
  if (subMenu.children.length) {
    Array.from(subMenu.children).forEach((item: any, index) => {
      if (item.classList.contains(headerNavCSS.hasItems)) {
        if (index === 0 && state.isDesktop && isExpandedMenuItem) {
          selectSubMenuItem(item);
        } else {
          unSelectSubMenuItem(item);
        }
      }
    });
  }
}

function enableOverlayClick() {
  const overlay = $(`.${headerNavCSS.overlay}`);
  if (overlay) {
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        closeMobileMenu();
        collapseAllMenus();
        setMenuExpanded(false);
      }
    });
  }
}

function makeHeaderWithoutHeight() {
  const headerElement = getHeader();
  if (headerElement) {
    headerElement.classList.add(headerCSS.zeroHeight);
  }
}

function showOverlay() {
  const overlayElement = $(`.${headerNavCSS.overlay}`)!;
  if (overlayElement && !overlayElement.classList.contains(headerNavCSS.showOverlay)) {
    overlayElement.classList.add(headerNavCSS.showOverlay);
    document.body.classList.add(headerNavCSS.fixedBody);
    state.trapFocusHandler = trapFocusOnOpenedHeader();
  }
}

function hideOverlay() {
  const overlayElement = $(`.${headerNavCSS.overlay}`)!;
  if (overlayElement && overlayElement.classList.contains(headerNavCSS.showOverlay)) {
    overlayElement.classList.remove(headerNavCSS.showOverlay);
    document.body.classList.remove(headerNavCSS.fixedBody);
    if (state.trapFocusHandler) {
      removeTrapFocus(state.trapFocusHandler);
      state.trapFocusHandler = null;
    }
  }
}

function handleWindowResize() {
  const currentSize = window.innerWidth > MOBILE_MAX_WIDTH;
  if (state.isDesktop !== currentSize) {
    state.isDesktop = currentSize;
    changeTabIndexOfSecondLevelButtons();
    reorderHeaderElements();
    collapseAllMenus();
    setMenuExpanded(false);
  }
}

function changeTabIndexOfSecondLevelButtons() {
  const openThirdMenuButtons = $$('[data-hook="wix-header-open-third-menu"]') as Element[];
  openThirdMenuButtons.forEach((button) => {
    const tabindex = state.isDesktop ? '-1' : '0';
    button.setAttribute('tabindex', tabindex);
  });
}

function reorderHeaderElements() {
  const userMenu = $('[data-hook="userMenu"]');
  if (userMenu) {
    const container = userMenu.parentElement;
    if (container) {
      container.removeChild(userMenu);
      if (state.isDesktop) {
        container.appendChild(userMenu);
      } else {
        container.insertBefore(userMenu, container.firstChild);
      }
    }
  }
}

function trapFocus(firstElement: HTMLElement | (() => HTMLElement), lastElement: HTMLElement | (() => HTMLElement)) {
  const handler = (e: KeyboardEvent) => {
    const isTabPressed = e.key === 'Tab' || e.keyCode === 9;
    const firstFocusableElement = typeof firstElement === 'function' ? firstElement() : firstElement;
    const lastFocusableElement = typeof lastElement === 'function' ? lastElement() : lastElement;

    if (!isTabPressed) {
      return;
    }

    if (e.shiftKey) {
      // if shift key pressed for shift + tab combination
      if (document.activeElement === firstFocusableElement) {
        lastFocusableElement.focus();
        e.preventDefault();
      }
    } else {
      // if tab key is pressed
      if (document.activeElement === lastFocusableElement) {
        firstFocusableElement.focus();
        e.preventDefault();
      }
    }
  };
  document.addEventListener('keydown', handler);
  return handler;
}

function removeTrapFocus(handler: (e: KeyboardEvent) => void) {
  document.removeEventListener('keydown', handler);
}

function trapFocusOnOpenedHeader() {
  const firstFocusableElement = $(`[data-hook="wix-header-logo-link"]`) as HTMLAnchorElement;
  const getLastFocusableElement = () => {
    const isLoggedIn = document.body.classList.contains('logged_in');
    const isDesktopView = window.innerWidth > MOBILE_MAX_WIDTH;
    if (isLoggedIn) {
      if (isDesktopView) {
        return getAccountMenuButton();
      } else {
        const languageButton = $('#main-menu-item-languages') as HTMLButtonElement;
        if (languageButton.getAttribute('aria-expanded') === 'true') {
          const languageLinks = $$(`[data-hook="wix-header-language-submenu-wrapper"] .${headerNavCSS.link}`) as HTMLAnchorElement[];
          return languageLinks[languageLinks.length - 1];
        } else {
          return languageButton;
        }
      }
    } else {
      const loginButtons = $$('[data-hook="login"]') as HTMLElement[];
      return loginButtons[isDesktopView ? 0 : 1];
    }
  };

  return trapFocus(firstFocusableElement, getLastFocusableElement);
}

function isFocusEnabled() {
  return document.body.classList.contains('focused');
}

function hideMainLinksOnDesktop() {
  const mainMenu = getMainMenuWrapper();
  mainMenu.classList.add(headerNavCSS.hideMainLinks);
}

function addOriginParamToLoginLink() {
  const desktopLink = getDesktopLoginLink();
  desktopLink.href = desktopLink.href.concat(`&originUrl=${encodeURIComponent(location.href)}`);
}

function addEventListenersToCategoryMenuButtons() {
  const buttons = getNavigationMenus();
  buttons.forEach((button) => {
    stopClickPropagation(button.parentElement!);
    fireClickEventWhenSpaceIsPressed(button);
  });
}

// function specially for WixSites which disable executing viewer event listener
// should be applied only for <a href="#"> to disable navigation. And should be connected as last in order
function stopClickPropagation(element: HTMLElement) {
  element.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopImmediatePropagation();
  });
}

function fireClickEventWhenSpaceIsPressed(element: HTMLElement) {
  element.addEventListener('keydown', (event) => {
    if (document.activeElement === element && event.code === 'Space') {
      element.click();
      event.preventDefault();
    }
  });
}
